import React, { useEffect, useState } from 'react'
import { useWidgetSite } from '../../../contexts/WidgetSiteContext'
import { useDebounce } from '../../../hooks'
import { fetchCarouselPreview, PreviewCard } from '../../../services/api'
import formStyles from '../../../styles/forms.module.scss'
import type { CarouselMetadata } from './CarouselEditor'
import styles from './CardCarouselEditor.module.scss'

interface CarouselPreviewPaneProps {
  metadata: CarouselMetadata
}

// Low-fidelity live preview: posts the in-progress config to the CMS API,
// which proxies to ACE's previewWidgetItems, and renders the resolved cards as
// plain tiles. Real content, deliberately not the real app's styling.
const CarouselPreviewPane: React.FC<CarouselPreviewPaneProps> = ({ metadata }) => {
  const { siteId } = useWidgetSite()
  // Only the content-resolving fields matter to ACE; presentation edits (title,
  // card_fields, show_more — Name mirrors into title on every keystroke) must
  // not trigger a fresh preview round-trip.
  const configKey = JSON.stringify({
    sources: metadata.sources,
    filters: metadata.filters,
    sort: metadata.sort,
    limit: metadata.limit,
    ended_grace_minutes: metadata.ended_grace_minutes,
  })
  const debouncedKey = useDebounce(configKey, 400)
  const ready = !!siteId && ((metadata.sources as string[] | undefined) ?? []).length > 0

  const [cards, setCards] = useState<PreviewCard[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!ready) {
      setCards([])
      setError(null)
      return
    }
    let active = true
    setLoading(true)
    setError(null)
    fetchCarouselPreview(siteId, JSON.parse(debouncedKey), 20)
      .then(res => { if (active) setCards(res.cards) })
      .catch(err => { if (active) setError(err instanceof Error ? err.message : 'Preview failed') })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [debouncedKey, ready, siteId])

  if (!ready) {
    return <p className={formStyles.hint}>Add a source to preview this rail.</p>
  }

  return (
    <div className={styles.previewPane}>
      <div className={styles.previewHeader}>
        <span>Live preview</span>
        {loading && <span className={styles.previewStatus}>Loading…</span>}
      </div>

      {error && <p className={styles.previewError}>{error}</p>}
      {!error && !loading && cards.length === 0 && (
        <p className={formStyles.hint}>No matching content for this config.</p>
      )}

      <div className={styles.previewStrip}>
        {cards.map(card => (
          <div key={card.cardId} className={styles.previewCard}>
            {card.image?.cdnUrl && (
              <img src={card.image.cdnUrl} alt={card.image.altText ?? card.title} />
            )}
            {card.badge && <span className={styles.previewBadge}>{card.badge}</span>}
            <div className={styles.previewCardTitle}>{card.title}</div>
            {card.subtitle && <div className={styles.previewCardSub}>{card.subtitle}</div>}
          </div>
        ))}
      </div>
    </div>
  )
}

export default CarouselPreviewPane
